import * as React from "react"
import { cn } from "@/lib/utils"

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'bordered' | 'elevated' | 'flat' | 'glass' | 'solid' | 'gradient' | 'outline';
  hoverable?: boolean;
  fullWidth?: boolean;
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  elevation?: 'none' | 'sm' | 'md' | 'lg';
  hover?: 'lift' | 'grow' | 'glow';
  animation?: 'fadeIn' | 'slideIn' | 'scaleIn';
}

const cardVariants = {
  default: "bg-white dark:bg-gray-900 dark:border-gray-800 border border-gray-200",
  bordered: "bg-white dark:bg-gray-900 dark:border-gray-800 border border-gray-300",
  elevated: "bg-white dark:bg-gray-900 dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] shadow-md",
  flat: "bg-gray-50 dark:bg-gray-800 dark:border-gray-700 border border-gray-100",
  glass: "bg-white/70 dark:bg-black/60 backdrop-blur-md border dark:border-gray-800/60 border-white/20",
  solid: "bg-neutral-800 dark:bg-black text-white",
  gradient: "bg-gradient-to-r from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800 text-white",
  outline: "bg-transparent dark:border-gray-700 border border-gray-300",
};

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = "default", hoverable, fullWidth, radius = "lg", elevation = "none", hover, animation, ...props }, ref) => {
    // Get radius styles
    const radiusStyles = {
      "premium-radius-none": radius === "none",
      "premium-radius-sm": radius === "sm",
      "premium-radius-md": radius === "md",
      "premium-radius-lg": radius === "lg",
      "premium-radius-full": radius === "full",
    };
    
    // Use the design system radius class
    const radiusClasses = radius ? `premium-radius-${radius}` : "premium-radius-card";

    return (
      <div
        ref={ref}
        className={cn(
          "overflow-hidden",
          radiusClasses,
          {
            "transition-shadow hover:shadow-lg": hoverable,
            "w-full": fullWidth,
            "w-auto": fullWidth === false,
            ...radiusStyles,
            "shadow-none": elevation === "none",
            "shadow-sm": elevation === "sm",
            "shadow-md": elevation === "md",
            "shadow-lg": elevation === "lg",
            "animate-in fade-in-50": animation === "fadeIn",
            "animate-in slide-in-from-bottom-5": animation === "slideIn",
            "animate-in zoom-in-95": animation === "scaleIn",
            "hover:shadow-lg transform hover:-translate-y-1 transition-all": 
              hover === "lift",
            "hover:scale-105 transition-transform": 
              hover === "grow",
            "hover:shadow-lg hover:shadow-primary-500/10 transition-shadow": 
              hover === "glow",
          },
          className
        )}
        {...props}
      />
    )
  }
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col space-y-1.5 p-6", className)}
      {...props}
    />
  )
)
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-2xl font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  )
)
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
)
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
  )
)
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-center p-6 pt-0", className)}
      {...props}
    />
  )
)
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
